import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { smiUrl } from 'components';
import { catchError, Observable, of } from 'rxjs';
import { retryUnlessPlanRefusal } from '@brand-lolly/app/social-media-influencer/modules/social-listening/utils/social-listening-refusal.util';

export interface CompetitorKeywordItem {
	id?: string;
	competitor_id?: string;
	keyword_id?: string;
	keyword_type: 'include' | 'exclude';
	match_type?: string;
	text: string;
}

export interface CompetitorProjectItem {
	id: string;
	name: string;
	status?: string;
	/**
	 * When collection last produced anything, for the header's status block.
	 * Optional because the field is being added to this response separately —
	 * absent, the header says "Not collected yet" rather than nothing at all.
	 */
	last_collected_at?: string | null;
	/**
	 * Why the entity's last collection run collected nothing, when it was
	 * stopped: `entity_budget`, `account_pool`, `credit_balance`,
	 * `free_ceiling` or `daily_allowance`. Null or absent once a run reserves.
	 */
	last_run_stopped_by?: string | null;
	/**
	 * The Social Listening brand this competitor project belongs to.
	 *
	 * Nullable and `ON DELETE SET NULL` in the schema, so a project whose brand was
	 * removed keeps working and simply cannot say where its creators came from.
	 * The API has always returned it; nothing on the frontend read it until the
	 * campaign picker needed provenance.
	 */
	brand_id?: string | null;
	competitors: {
		id: string;
		name: string;
		keywords?: CompetitorKeywordItem[];
	}[];
}

@Injectable({ providedIn: 'root' })
export class GetSocialListeningCompetitorProjectsService {
	constructor(private http: HttpClient) {}

	/**
	 * Lists the user's Social Listening competitor projects. On error returns
	 * an empty array (NOT mock data) so callers can safely use it to decide
	 * whether the user has any competitor project.
	 */
	fetchAll(): Observable<CompetitorProjectItem[]> {
		return this.read().pipe(
			catchError(() => of<CompetitorProjectItem[]>([]))
		);
	}

	/**
	 * {@link fetchAll} without the empty fallback, so the redirect guard can
	 * tell "no project" from "refused for the plan".
	 */
	read(): Observable<CompetitorProjectItem[]> {
		const url = smiUrl()
			.add('social-listening')
			.add('competitors')
			.add('projects')
			.done();

		// First SL navigation after load can race the auth/subscription
		// context; retry the transient failure before reading it as "no
		// project" (which would misredirect to competitors-setup).
		return this.http
			.get<CompetitorProjectItem[]>(url)
			.pipe(retryUnlessPlanRefusal());
	}

	fetchOne(projectId: string): Observable<CompetitorProjectItem> {
		const url = smiUrl()
			.add('social-listening')
			.add('competitors')
			.add('projects')
			.add(projectId)
			.done();

		return this.http.get<CompetitorProjectItem>(url);
	}
}
